import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import {
  Card,
  CardContent,
  CardFooter,
  CardHeader,
  CardTitle,
} from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { cn } from '@/components/ui/cn';
import { countriesQuery, type Country } from '@/data';
import { scoreCountries } from '../scoring';
import { useQuizStore } from '../state';
import { uiLang } from '@/i18n/lang';

export interface SocialProofInterstitialProps {
  onContinue: () => void;
  className?: string;
}

/**
 * Mid-quiz interstitial (between Q4 and Q5).
 *
 * Replaces an earlier hardcoded fake-social-proof block ("847 people chose
 * Italy…") with an honest preview: runs scoreCountries() against the user's
 * current partial answers and shows the three leading countries. Same Quiet
 * Atlas visual treatment — paper-2 KPI tiles, serif headline, mono labels.
 */
export function SocialProofInterstitial({
  onContinue,
  className,
}: SocialProofInterstitialProps) {
  const { t, i18n } = useTranslation();
  const lang = uiLang(i18n.language);
  const answers = useQuizStore((s) => s.answers);
  const { data: file } = useQuery(countriesQuery());

  const answersCount = Object.values(answers).filter(
    (a) => a && a.optionIds.length > 0,
  ).length;

  const byCode = useMemo<Map<string, Country>>(
    () => new Map((file?.countries ?? []).map((c) => [c.code, c])),
    [file],
  );

  const top = useMemo(() => {
    if (!file || answersCount === 0) return [];
    const result = scoreCountries(file.countries, answers, 3);
    return result.topMatches.slice(0, 3);
  }, [file, answers, answersCount]);

  return (
    <Card className={cn('w-full bg-paper-2', className)}>
      <CardHeader className="text-center">
        <CardTitle>{t('quiz.interstitial.title')}</CardTitle>
      </CardHeader>
      <CardContent className="flex flex-col items-center gap-5 text-center text-sm text-ink-700">
        <p className="max-w-md leading-relaxed">
          {top.length > 0
            ? t('quiz.interstitial.subtitle', { answered: answersCount })
            : t('quiz.interstitial.subtitleNone')}
        </p>

        {top.length > 0 ? (
          <ul className="grid w-full grid-cols-1 gap-2 sm:grid-cols-3">
            {top.map((match, idx) => {
              const country = byCode.get(match.countryCode);
              const name = country
                ? lang === 'ru'
                  ? country.name_ru
                  : country.name_en
                : match.countryCode;
              const percent = Math.round(match.score * 100);
              return (
                <li
                  key={match.countryCode}
                  className="flex flex-col items-center gap-1 rounded-md border border-ink-100 bg-paper px-3 py-3"
                  data-testid={`interstitial-rank-${idx + 1}`}
                >
                  <span className="flex items-baseline gap-2">
                    <span
                      aria-hidden="true"
                      className="font-mono text-[11px] tabular-nums text-ink-400"
                    >
                      {t('quiz.interstitial.rankPrefix')}
                      {idx + 1}
                    </span>
                    <span className="text-xl leading-none" aria-hidden="true">
                      {country?.flag_emoji ?? ''}
                    </span>
                  </span>
                  <span className="font-display text-lg font-medium italic text-ink-900">
                    {name}
                  </span>
                  <span className="font-mono text-sm font-semibold tabular-nums text-terra-600">
                    {percent}%
                  </span>
                </li>
              );
            })}
          </ul>
        ) : null}
      </CardContent>
      <CardFooter className="justify-center pt-2">
        <Button variant="primary" onClick={onContinue}>
          {t('quiz.interstitial.continue')}
          <span aria-hidden="true" className="font-mono text-xs opacity-80">
            →
          </span>
        </Button>
      </CardFooter>
    </Card>
  );
}
